import { Injectable, inject } from '@angular/core';
import { GungImageUrlService } from 'gung-common';
import { PunchoutBuildContext, PunchoutError, PunchoutOrderRow, PunchoutPayload } from '../../../models/punchout';
import { Product } from '../../../models';
import { PunchoutProtocolHandler } from './punchout-protocol.handler';

@Injectable({ providedIn: 'root' })
export class OciPunchoutHandler extends PunchoutProtocolHandler {
  readonly protocol = 'oci';
  readonly identifier: string = 'oci-default';
  readonly label: string = 'OCI (default)';

  protected gungImageUrlService = inject(GungImageUrlService);

  protected indexBase = 1;
  protected productIdField = 'EXT_PRODUCT_ID';
  protected unit = 'EA';
  protected priceUnit = '1';
  protected attachmentImageSize = '200x200';

  protected getRowId(row: PunchoutOrderRow): string {
    return row.productId;
  }

  protected getImageUrl(product: Product): string {
    if (!product?.extra?.images?.length) {
      return '';
    }
    const image = product.extra.images[0];
    if (!image.s3Uri || !image.s3ETag) {
      return '';
    }
    return this.gungImageUrlService.getUrl(encodeURIComponent(image.s3Uri), {
      size: this.attachmentImageSize,
      etag: image.s3ETag,
      fill: 'white',
      background_color: 'white'
    });
  }

  protected mapRow(
    row: PunchoutOrderRow,
    index: number,
    extras: { punchoutOrderId?: string }
  ): { [s: string]: string } {
    const i = index + this.indexBase;
    const quantity = row.quantity ?? 1;
    if (!Number.isFinite(quantity)) {
      throw new PunchoutError('PUNCHOUT_OCI_INVALID_QUANTITY', 'OCI punchout quantity is invalid.');
    }
    if (!Number.isFinite(row.price)) {
      throw new PunchoutError('PUNCHOUT_OCI_INVALID_PRICE', 'OCI punchout price is invalid.');
    }
    const fields: { [s: string]: string } = {
      [`NEW_ITEM-DESCRIPTION[${i}]`]: row.product?.name ?? '',
      [`NEW_ITEM-QUANTITY[${i}]`]: String(quantity),
      [`NEW_ITEM-UNIT[${i}]`]: this.unit,
      [`NEW_ITEM-PRICE[${i}]`]: row.price.toFixed(6),
      [`NEW_ITEM-PRICEUNIT[${i}]`]: this.priceUnit,
      [`NEW_ITEM-CURRENCY[${i}]`]: row.currency ?? '',
      [`NEW_ITEM-${this.productIdField}[${i}]`]: this.getRowId(row),
      [`NEW_ITEM-ATTACHMENT[${i}]`]: this.getImageUrl(row.product)
    };
    if (extras.punchoutOrderId) {
      fields[`NEW_ITEM-CUST_FIELD1[${i}]`] = extras.punchoutOrderId;
    }
    return fields;
  }

  buildPayload(rows: PunchoutOrderRow[], context?: PunchoutBuildContext): PunchoutPayload {
    const extras = {
      punchoutOrderId: this.readPunchoutOrderId(context?.checkoutObject?.extra)
    };
    const fields: { [s: string]: string } = {};
    rows.forEach((row, index) => {
      Object.assign(fields, this.mapRow(row, index, extras));
    });
    return { kind: 'oci-form-post', fields };
  }

  protected readPunchoutOrderId(extra: { [s: string]: unknown } | undefined): string | undefined {
    const value = extra?.['punchoutOrderId'];
    return typeof value === 'string' && value ? value : undefined;
  }
}
